import React from "react";
import history from "./utils/history";
import { Router, Switch, Route } from "react-router-dom";
import urls from "./global/constants/UrlConstants";
import withClearCache from "./ClearCache";
import { useAppDispatch, useAppSelector } from "./utils/hooks";
import { selectAuthenticated } from "./redux/authSlice";
import { Box } from "@material-ui/core";
import Notifications from "./utils/notifications";
import PrivateRoute from "./global/components/PrivateRoute/PrivateRoute";
import Loading from "./screens/Shared/Loading";
import { useLocationChange } from "./utils/useLocationChange";
import {
  addIdemp,
  selectRestaurantId,
  selectSessionId,
  selectTableNumber,
} from "./redux/menuSlice";
import {
  selectSplitPayEnabledStatus,
  updateCheckoutState,
} from "./redux/generalSlice";
import strings from "./global/constants/StringConstants";
import LocationOffline from "./screens/Shared/LocationOffline/LocationOffline";
import UTTLocked from "./screens/Shared/UTTLocked/UTTLocked";
import "react-loading-skeleton/dist/skeleton.css";
import Sockets from "./utils/sockets";
import { emitSocketEvent } from "./helpers/methods";
import { usePostHog } from "posthog-js/react";
import lazyWithRetry from "./utils/lazyWithRetry";

const Auth = lazyWithRetry(() => import("./screens/Auth/Auth"));
const RestaurantClosed = lazyWithRetry(
  () => import("./screens/Shared/RestaurantClosed/RestaurantClosed")
);
const Item = lazyWithRetry(() => import("./screens/Item/Item"));
const PaymentMethods = lazyWithRetry(
  () => import("./screens/PaymentMethod/PaymentMethods")
);
const Menu = lazyWithRetry(() => import("./screens/Menu/Menu"));
const MenuWrapper = lazyWithRetry(() => import("./screens/Menu/MenuWrapper"));
const PageNotFound = lazyWithRetry(
  () => import("./screens/PageNotFound/PageNotFound")
);
const Cart = lazyWithRetry(() => import("./screens/Cart/Cart"));
const Payment = lazyWithRetry(() => import("./screens/Payments/Payments"));
const ThankYou = lazyWithRetry(() => import("./screens/Thankyou/ThankYou"));
const TableCheckInput = lazyWithRetry(() => import("./screens/TableCheckInput/TableCheckInput"));
const NotFound = lazyWithRetry(() => import("./screens/NotFound/NotFound"));

const App = () => {
  return <ClearCacheComponent />;
};

const MainApp = () => {
  // const postHog = usePostHog();
  // const googleTranslateElementInit = () => {
  //   //@ts-ignore
  //   new window.google.translate.TranslateElement(
  //     {
  //       pageLanguage: "en",
  //       includedLanguages: "en,es,zh-CN,tl,vi,fr,ar,ru,ko,de",
  //       autoDisplay: false,
  //     },
  //     "google_translate_element"
  //   );
  //   const element = document.querySelector("#google_translate_element select");
  //   if (element) {
  //     //@ts-ignore
  //     const index = Array.from(element.options).findIndex(
  //       (language: any) => language.value === "en"
  //     );
  //     //@ts-ignore
  //     element.selectedIndex = index;
  //     element.dispatchEvent(new Event("change"));
  //   }
  // };

  // useEffect(() => {
  //   var addScript = document.createElement("script");
  //   addScript.setAttribute(
  //     "src",
  //     "//translate.google.com/translate_a/element.js?cb=googleTranslateElementInit"
  //   );
  //   document.body.appendChild(addScript);
  //   //@ts-ignore
  //   window.googleTranslateElementInit = googleTranslateElementInit;
  // }, []);

  // aws cloudwatch RUM
  try {
    // if (urls.PROD) {
    // postHog.startSessionRecording();
    //   H.init(strings.highLightProjectIdForProd, {
    //     tracingOrigins: ["glydegateway.com"],
    //     networkRecording: {
    //       enabled: true,
    //       recordHeadersAndBody: true,
    //     },
    //   });
    // }
  } catch (error) {
    // Ignore errors thrown during CloudWatch RUM web client initialization
  }

  return (
    <Box
      style={{
        margin: "0 auto",
      }}
    >
      <Router history={history}>
        <div id="google_translate_element"></div>
        <GlydeSwitch />
      </Router>
      <Notifications />
      <UTTLocked />
    </Box>
  );
};

const GlydeSwitch = () => {
  const urlParams = window.location.search;
  const restaurantIdFromURL = new URLSearchParams(urlParams).get("restId");
  const tableNumberFromURL = new URLSearchParams(urlParams).get("tablenum");
  const authenticated = useAppSelector(selectAuthenticated);
  const sessionIdFromRedux = useAppSelector(selectSessionId);
  const restaurantIdFromRedux = useAppSelector(selectRestaurantId);
  const tableNumberFromRedux = useAppSelector(selectTableNumber);
  const splitPayEnabled = useAppSelector(selectSplitPayEnabledStatus);
  const dispatch = useAppDispatch();

  // Handle conditional routing to TableCheckInput
  React.useEffect(() => {
    const currentPath = window.location.pathname;
    // Only redirect if we're on the landing page and have restId but no tableNum
    if (
      currentPath === urls.landingViewPath &&
      restaurantIdFromURL &&
      !tableNumberFromURL &&
      !tableNumberFromRedux
    ) {
      history.push(urls.tableCheckInputViewPath + window.location.search);
    }
  }, [restaurantIdFromURL, tableNumberFromURL, tableNumberFromRedux]);

  useLocationChange((location: any, prevLocation: any) => {
    const storage = JSON.parse(localStorage.getItem("state") ?? "{}");
    if (
      storage.hasOwnProperty("general") &&
      (!storage.general.hasOwnProperty("checkoutState") ||
        storage.general.hasOwnProperty("retryMode"))
    ) {
      dispatch(
        updateCheckoutState({
          buttonText: "Checkout",
          retryMode: 0,
          errorIn: "",
        })
      );
    }
    if (
      storage.hasOwnProperty("menu") &&
      !storage.menu.hasOwnProperty("idemp")
    ) {
      dispatch(
        addIdemp({
          mode: "pay",
          key: "",
          hash: "",
        })
      );
    }
    if (
      restaurantIdFromURL &&
      tableNumberFromURL &&
      (restaurantIdFromURL !== restaurantIdFromRedux ||
        tableNumberFromURL !== tableNumberFromRedux)
    ) {
      // Loads in new restaurant
      return;
    }
    if (!splitPayEnabled) return;
    if (prevLocation?.pathname !== urls.paymentViewPath) return; // ignore
    // User navigated away from payment page
    // console.log("navigated away from payment page, unlock items");
    dispatch(
      updateCheckoutState({
        buttonText: "Checkout",
        retryMode: 0,
        errorIn: "",
      })
    );
    if (
      location?.pathname === urls.landingViewPath &&
      location?.state?.hasOwnProperty("unlockItems") &&
      !location?.state?.unlockItems
    )
      return;
    emitSocketEvent("emitUnlockItems");
  });

  return (
    <React.Suspense fallback={<Loading />}>
      <LocationOffline />
      <Sockets
        restaurantId={restaurantIdFromRedux}
        sessionId={sessionIdFromRedux}
        tableNumber={tableNumberFromRedux}
      />
      <Switch>
        <Route exact path={urls.authViewPath} component={Auth} />
        <Route
          exact
          path={[urls.landingViewPath, urls.menuViewPath]}
          component={MenuWrapper}
        />
         <Route
          exact
          path={urls.tableCheckInputViewPath}
          component={TableCheckInput}
        />
        <PrivateRoute
          exact
          isLoggedIn={authenticated}
          path={urls.paymentMethodViewPath}
          component={PaymentMethods}
        />
        <Route
          exact
          path={urls.restaurantClosedViewPath}
          component={RestaurantClosed}
        />
        <Route exact path={urls.itemViewPath} component={Item} />
        <Route exact path={urls.cartViewPath} component={Cart} />
        <PrivateRoute
          exact
          isLoggedIn={authenticated}
          path={urls.paymentViewPath}
          component={Payment}
        />
        <Route exact path={urls.thankYouViewPath} component={ThankYou} />
        <Route
          exact
          path={urls.apple_developer_merchantid_domain_association}
        />
        <Route
          exact
          path={urls.apple_developer_merchantid_domain_association_txt}
        />
        <Route path="*" component={PageNotFound} />
      </Switch>
    </React.Suspense>
  );
};

const ClearCacheComponent = withClearCache(MainApp);

export default App;
